"use client";

import { c as _c } from "react/compiler-runtime";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, Link as MuiLink, Paper, Stack, Typography, useTheme } from "@mui/material";
import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import { MOBILE_PHASE_NAV_HEIGHT } from "#/features/playground/ui/MobilePhaseNavBar";
import { useI18nContext } from "#/shared/hooks";
import { useMobileLayout } from "#/shared/hooks/useMobileLayout";
import { glassOverlaySx } from "#/shared/ui/styles/glassOverlayStyles";
type CookieConsentBannerProps = {
  isSettingsView: boolean;
  onAcceptAll: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onRejectNonEssential: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onClose: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Visual surface used by optional dismiss effects (e.g. DOM disintegrate). */
  surfaceRef?: React.Ref<HTMLDivElement>;
};
export const CookieConsentBanner: React.FC<CookieConsentBannerProps> = t0 => {
  const $ = _c(65);
  const {
    isSettingsView,
    onAcceptAll,
    onRejectNonEssential,
    onClose,
    surfaceRef
  } = t0;
  const {
    LL
  } = useI18nContext();
  const theme = useTheme();
  const router = useRouter();
  const isMobileLayout = useMobileLayout();
  const isPlaygroundRoute = router.pathname.startsWith("/playground");
  const reserveMobileNavSpace = isMobileLayout && isPlaygroundRoute;
  const t1 = reserveMobileNavSpace ? `calc(${MOBILE_PHASE_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px))` : 0;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      xs: 2,
      sm: 3
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = reserveMobileNavSpace ? 2 : "calc(16px + env(safe-area-inset-bottom, 0px))";
  let t4;
  if ($[1] !== t1 || $[2] !== t3) {
    t4 = {
      position: "fixed",
      bottom: t1,
      left: 0,
      right: 0,
      zIndex: 1200,
      px: t2,
      pb: t3,
      pt: 2,
      pointerEvents: "none"
    };
    $[1] = t1;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      xs: 2,
      sm: 3
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== theme) {
    t6 = glassOverlaySx(theme);
    $[5] = theme;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t6) {
    t7 = {
      pointerEvents: "auto",
      maxWidth: 960,
      mx: "auto",
      px: t5,
      py: 2,
      borderRadius: 2,
      position: "relative",
      ...t6
    };
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== LL || $[10] !== isSettingsView || $[11] !== onClose) {
    t8 = isSettingsView ? <IconButton aria-label={LL.COOKIE_SETTINGS_CLOSE()} onClick={onClose} size="small" sx={{
      position: "absolute",
      top: 8,
      right: 8
    }}><CloseIcon fontSize="small" /></IconButton> : null;
    $[9] = LL;
    $[10] = isSettingsView;
    $[11] = onClose;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      xs: "column",
      sm: "row"
    };
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      xs: "stretch",
      sm: "center"
    };
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = isSettingsView ? 4 : 0;
  let t12;
  if ($[15] !== t11) {
    t12 = {
      alignItems: t10,
      justifyContent: "space-between",
      pr: t11
    };
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      minWidth: 0
    };
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      mb: 0.5
    };
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== LL || $[20] !== isSettingsView) {
    t15 = isSettingsView ? LL.COOKIE_SETTINGS_TITLE() : LL.COOKIE_CONSENT_TITLE();
    $[19] = LL;
    $[20] = isSettingsView;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== t15) {
    t16 = <Typography id="cookie-consent-title" variant="subtitle2" component="h2" sx={t14}>{t15}</Typography>;
    $[22] = t15;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== LL || $[25] !== isSettingsView) {
    t17 = isSettingsView ? LL.COOKIE_SETTINGS_MESSAGE() : LL.COOKIE_CONSENT_MESSAGE();
    $[24] = LL;
    $[25] = isSettingsView;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      fontWeight: 600
    };
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== LL) {
    t19 = LL.PRIVACY_POLICY();
    $[28] = LL;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] !== t19) {
    t20 = <MuiLink component={Link} href="/privacy" underline="hover" color="inherit" sx={t18}>{t19}</MuiLink>;
    $[30] = t19;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] !== t17 || $[33] !== t20) {
    t21 = <Typography id="cookie-consent-description" variant="body2" color="text.secondary">{t17}{" "}{t20}.</Typography>;
    $[32] = t17;
    $[33] = t20;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== t16 || $[36] !== t21) {
    t22 = <Box sx={t13}>{t16}{t21}</Box>;
    $[35] = t16;
    $[36] = t21;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      xs: "column",
      sm: "row"
    };
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  let t24;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = {
      flexShrink: 0
    };
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  let t25;
  if ($[40] !== LL) {
    t25 = LL.COOKIE_REJECT_NON_ESSENTIAL();
    $[40] = LL;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  let t26;
  if ($[42] !== onRejectNonEssential || $[43] !== t25) {
    t26 = <Button variant="outlined" color="primary" onClick={onRejectNonEssential}>{t25}</Button>;
    $[42] = onRejectNonEssential;
    $[43] = t25;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  let t27;
  if ($[45] !== LL) {
    t27 = LL.COOKIE_ACCEPT_ALL();
    $[45] = LL;
    $[46] = t27;
  } else {
    t27 = $[46];
  }
  let t28;
  if ($[47] !== onAcceptAll || $[48] !== t27) {
    t28 = <Button variant="outlined" color="primary" onClick={onAcceptAll}>{t27}</Button>;
    $[47] = onAcceptAll;
    $[48] = t27;
    $[49] = t28;
  } else {
    t28 = $[49];
  }
  let t29;
  if ($[50] !== t26 || $[51] !== t28) {
    t29 = <Stack direction={t23} spacing={1} sx={t24}>{t26}{t28}</Stack>;
    $[50] = t26;
    $[51] = t28;
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  let t30;
  if ($[53] !== t12 || $[54] !== t22 || $[55] !== t29) {
    t30 = <Stack direction={t9} spacing={2} sx={t12}>{t22}{t29}</Stack>;
    $[53] = t12;
    $[54] = t22;
    $[55] = t29;
    $[56] = t30;
  } else {
    t30 = $[56];
  }
  let t31;
  if ($[57] !== surfaceRef || $[58] !== t30 || $[59] !== t7 || $[60] !== t8) {
    t31 = <Paper ref={surfaceRef} elevation={0} sx={t7}>{t8}{t30}</Paper>;
    $[57] = surfaceRef;
    $[58] = t30;
    $[59] = t7;
    $[60] = t8;
    $[61] = t31;
  } else {
    t31 = $[61];
  }
  let t32;
  if ($[62] !== t31 || $[63] !== t4) {
    t32 = <Box component="section" role="region" aria-labelledby="cookie-consent-title" aria-describedby="cookie-consent-description" sx={t4}>{t31}</Box>;
    $[62] = t31;
    $[63] = t4;
    $[64] = t32;
  } else {
    t32 = $[64];
  }
  return t32;
};